/**
 * The opaque, grey-outlined, elevated clone of the column being dragged in the v8 {@link DataTable}.
 *
 * It floats in a dnd-kit `DragOverlay` — a `position: fixed` portal OUTSIDE the table's horizontal
 * scroll container — so following the pointer can never inflate the table's scrollWidth (an in-place
 * transform instead causes runaway auto-scroll). Sizes (column width, header + per-row heights) are
 * measured from the live DOM at drag-start so the clone aligns with the real rows;
 * `pointer-events-none` keeps it from swallowing the drag.
 *
 * Renders ONLY the rows DataTable sliced into `rows` (the viewport-visible subset) — cloning the
 * whole loaded list makes pickup stutter and scales with row count. v8 cell/header content is
 * produced with `flexRender`.
 *
 * Static layout/spacing/typography live in Tailwind classes; only the MEASURED geometry (column
 * width, header/totals/row heights) and the column's `textAlign` ride as inline styles, since they
 * are computed at drag-start and can't be static classes.
 */
import { DragIndicator as DragIndicatorIcon } from 'components/icons';
import { flexRender, type Header, type Row } from '@tanstack/react-table';
import { type ReactNode } from 'react';
import type { DataTableColumnMeta } from './types';

type Props<DataType> = {
    /** The dragged column's v8 leaf `Header` (carries both the header render fn + the column id). */
    header: Header<DataType, unknown>;
    /** Visible rows, in render order, matched 1:1 to `rowHeights`. */
    rows: Row<DataType>[];
    /** Measured at drag-start so the clone lines up with the real column. */
    width: number;
    headerHeight: number;
    rowHeights: number[];
    /** Render the dragged column's total in the clone (it sits between header and rows, mirroring
     *  the totals row's position in the table). False ⇒ no totals row shown. */
    showTotals?: boolean;
    /** The current totals object passed to the column's `meta.renderTotal`. */
    totals?: unknown;
    /** Measured totals-cell height so the clone's totals row lines up with the real one. */
    totalsHeight?: number;
};

const DataTableColumnDragPreview = <DataType,>({
    header,
    rows,
    width,
    headerHeight,
    rowHeights,
    showTotals,
    totals,
    totalsHeight,
}: Props<DataType>) => {
    const column = header.column;
    const meta = (column.columnDef.meta ?? {}) as DataTableColumnMeta;
    const textAlign = meta.textAlign;
    // meta.renderTotal is typed `(totals: never)` in the generic-free augmentation; the real totals
    // shape is the consumer's, so cast to call it (same pattern as DataTable's totals-row render).
    const renderTotal = meta.renderTotal as ((totals: unknown) => ReactNode) | undefined;
    return (
        <div
            // Match the table's text baseline so plain-text cells render identically (richer cell
            // content — e.g. the money cells' currency lines — carries its own styling).
            className="text-fg new:border-0 new:outline-hidden new:bg-card new:shadow-drag pointer-events-none box-border cursor-grabbing overflow-hidden rounded-[2px] border border-solid border-[#9e9e9e] bg-white text-[0.875rem] leading-[1.375rem] tabular-nums shadow-[0_6px_18px_-4px_rgba(0,0,0,0.35)]"
            style={{ width }}
        >
            <div
                className="new:border-border-control new:bg-surface-subtle box-border flex items-center gap-1 overflow-hidden border-b border-solid border-[#9e9e9e] bg-[#f6f6f6] px-4 text-[13px] font-semibold"
                style={{ height: headerHeight }}
            >
                {/* Keep the grip visible on the floating clone so the column reads as "held by the
                    grip" while dragging (the source header's grip is hidden with its dimmed
                    placeholder). Mirrors SortableHeaderCell's grip, always shown here (no hover/focus
                    gate). The head's fill is spelled out (not the --dt-* var, which does not cascade into
                    the DragOverlay's fixed portal) so only the orange glyph reads, like the real grip. */}
                <span className="new:bg-surface-subtle flex size-6 shrink-0 items-center justify-center rounded-xl bg-[#f6f6f6]">
                    <DragIndicatorIcon className="text-secondary text-[18px]" />
                </span>
                <div className="w-full min-w-0 overflow-hidden whitespace-nowrap" style={{ textAlign }}>
                    {header.isPlaceholder ? null : flexRender(column.columnDef.header, header.getContext())}
                </div>
            </div>
            {showTotals ? (
                <div
                    className="new:border-border-control new:bg-card box-border flex items-center overflow-hidden border-b border-solid border-[#9e9e9e] bg-white px-4 font-semibold"
                    style={{ height: totalsHeight }}
                >
                    <div className="w-full min-w-0 overflow-hidden whitespace-nowrap" style={{ textAlign }}>
                        {renderTotal && totals !== undefined ? renderTotal(totals) : null}
                    </div>
                </div>
            ) : null}
            {rows.map((row, index) => {
                const cell = row.getVisibleCells().find((candidate) => candidate.column.id === column.id);
                return (
                    <div
                        key={row.id}
                        className="new:border-border box-border flex items-center overflow-hidden border-b border-solid border-[#00000014] px-4"
                        style={{ height: rowHeights[index] }}
                    >
                        {/* Inner block element reproduces the table cell's normal block/inline flow +
                            text-align, so multi-element content (e.g. a money cell's two stacked
                            currency lines) keeps stacking instead of collapsing into flex items. The
                            outer flex only centres it vertically (the cell's middle vertical-align). */}
                        <div className="w-full min-w-0 overflow-hidden" style={{ textAlign }}>
                            {cell ? flexRender(cell.column.columnDef.cell, cell.getContext()) : null}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default DataTableColumnDragPreview;
